import { Pencil, Plus, Trash2 } from "lucide-react";
import React from "react";

import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  PermissionGuard,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components";
import { useI18n } from "@/context/i18n";

import { usePaymentGatewaysAdmin } from "./payment-gateways.hooks";

export const PaymentGatewaysPage: React.FC = () => {
  const { t } = useI18n();
  const {
    gateways,
    isLoading,
    dialogOpen,
    setDialogOpen,
    editingId,
    form,
    openCreate,
    openEdit,
    closeDialog,
    onSubmit,
    confirmDelete,
    isSaving,
  } = usePaymentGatewaysAdmin();

  return (
    <div className="flex flex-col w-full space-y-6 p-4">
      <div className="flex flex-row items-start justify-between px-2">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t("paymentGateways.title")}
          </h1>
        </div>
        <PermissionGuard permission="payment_gateways:manage">
          <Button type="button" size="sm" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            {t("paymentGateways.button.new")}
          </Button>
        </PermissionGuard>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("paymentGateways.table.slug")}</TableHead>
                <TableHead>{t("paymentGateways.table.name")}</TableHead>
                <TableHead>{t("paymentGateways.table.active")}</TableHead>
                <TableHead className="text-right">
                  {t("paymentGateways.table.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-muted-foreground">
                    …
                  </TableCell>
                </TableRow>
              ) : (
                (gateways ?? []).map((g) => (
                  <TableRow key={g.id}>
                    <TableCell className="font-mono text-sm">{g.slug}</TableCell>
                    <TableCell>{g.name}</TableCell>
                    <TableCell>
                      {g.is_active ? (
                        <Badge variant="secondary">
                          {t("paymentGateways.status.active")}
                        </Badge>
                      ) : (
                        <Badge variant="outline">
                          {t("paymentGateways.status.inactive")}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <PermissionGuard permission="payment_gateways:manage">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(g.id)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => confirmDelete(g.id, g.name)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </PermissionGuard>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingId
                ? t("paymentGateways.dialog.editTitle")
                : t("paymentGateways.dialog.createTitle")}
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={onSubmit} className="space-y-4">
              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("paymentGateways.form.slug")}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={!!editingId}
                        autoComplete="off"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("paymentGateways.form.name")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("paymentGateways.form.description")}</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                    <FormLabel>{t("paymentGateways.form.active")}</FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={closeDialog}>
                  {t("generic.cancel")}
                </Button>
                <Button type="submit" disabled={isSaving}>
                  {t("generic.save")}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
